import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { GroundSupplier } from "@b2bportal/ground-api";
import {
  CarsAvailabilitySelectors,
  CarsAvailabilityActions,
} from "~core-cars/store";
import type {
  CarTypeFilter,
  FilterState,
  MinimumBagCount,
  PassengerFilter,
  PriceRange,
  Specification,
} from "~core-cars/types";

export const useCarsAvailabilityFilters = () => {
  const dispatch = useDispatch();

  const filterOptions = useSelector(CarsAvailabilitySelectors.getFilterOptions);
  const filters = useSelector(CarsAvailabilitySelectors.getFilters);
  const filteredCars = useSelector(CarsAvailabilitySelectors.getFilteredCars);
  const hasFilters = useSelector(CarsAvailabilitySelectors.hasFilters);
  const totalFilterCount = useSelector(
    CarsAvailabilitySelectors.getTotalFilterCount
  );

  const setCarTypeFilter = useCallback(
    (val: Array<CarTypeFilter>) =>
      dispatch(CarsAvailabilityActions.setCarTypeFilter(val)),
    [dispatch]
  );

  const setCancellationFilter = useCallback(
    (val: boolean) =>
      dispatch(CarsAvailabilityActions.setCancellationFilter(val)),
    [dispatch]
  );

  const setSpecificationFilter = useCallback(
    (val: Specification) =>
      dispatch(CarsAvailabilityActions.setSpecificationFilter(val)),
    [dispatch]
  );

  const setSupplierFilter = useCallback(
    (val: Array<GroundSupplier>) =>
      dispatch(CarsAvailabilityActions.setSupplierFilter(val)),
    [dispatch]
  );

  const setMinimumBagsFilter = useCallback(
    (val: MinimumBagCount) =>
      dispatch(CarsAvailabilityActions.setMinimumBagsFilter(val)),
    [dispatch]
  );

  const setPassengerFilter = useCallback(
    (val: PassengerFilter[]) =>
      dispatch(CarsAvailabilityActions.setPassengerFilter(val)),
    [dispatch]
  );

  const setPriceFilter = useCallback(
    (val: PriceRange) => dispatch(CarsAvailabilityActions.setPriceFilter(val)),
    [dispatch]
  );

  const resetCancellationFilter = useCallback(
    () => dispatch(CarsAvailabilityActions.resetCancellationFilter()),
    [dispatch]
  );

  const resetCarTypeFilter = useCallback(() => {
    dispatch(CarsAvailabilityActions.resetCarTypeFilter());
  }, [dispatch]);

  const resetMinimumBagsFilter = useCallback(
    () => dispatch(CarsAvailabilityActions.resetMinimumBagsFilter()),
    [dispatch]
  );

  const resetPassengerFilter = useCallback(
    () => dispatch(CarsAvailabilityActions.resetPassengerFilter()),
    [dispatch]
  );

  const resetPriceFilter = useCallback(
    () => dispatch(CarsAvailabilityActions.resetPriceFilter()),
    [dispatch]
  );

  const resetSupplierFilter = useCallback(
    () => dispatch(CarsAvailabilityActions.resetSupplierFilter()),
    [dispatch]
  );

  const resetFilters = useCallback(() => {
    dispatch(CarsAvailabilityActions.resetFilters());
  }, [dispatch]);

  const setAllFilters = useCallback(
    (localFilters: FilterState) => {
      dispatch(CarsAvailabilityActions.setAllFilters(localFilters));
    },
    [dispatch]
  );

  const context = useMemo(
    () => ({
      filterOptions,
      filters,
      filteredCars,
      hasFilters,
      totalFilterCount,
    }),
    [filterOptions, filters, filteredCars, hasFilters, totalFilterCount]
  );

  const handlers = useMemo(
    () => ({
      setCarTypeFilter,
      setCancellationFilter,
      setSpecificationFilter,
      setSupplierFilter,
      setMinimumBagsFilter,
      setPassengerFilter,
      setPriceFilter,
      resetCancellationFilter,
      resetCarTypeFilter,
      resetMinimumBagsFilter,
      resetPassengerFilter,
      resetPriceFilter,
      resetSupplierFilter,
      resetFilters,
      setAllFilters,
    }),
    [
      setCarTypeFilter,
      setCancellationFilter,
      setSpecificationFilter,
      setSupplierFilter,
      setMinimumBagsFilter,
      setPassengerFilter,
      setPriceFilter,
      resetCancellationFilter,
      resetCarTypeFilter,
      resetMinimumBagsFilter,
      resetPassengerFilter,
      resetPriceFilter,
      resetSupplierFilter,
      resetFilters,
      setAllFilters,
    ]
  );

  return {
    context,
    handlers,
  };
};
